<template>
    <div>
        <div :class="['user-or-class d-flex justify-content-between align-items-center',
        group ? `bg-light-${group.color}` : '']"  @click="handleHeaderClick()" :role="child ? 'button' : 'presentation'">
            <div class="d-flex gap-2 w-100 align-items-center">
                <div class="image" v-if="child">
                    <img  :src="child.image?.sizes.sm ?? '/img/avatar.png'" :alt="name" width="65px" height="65px">
                </div>
                <div class="icon py-3" v-else-if="group">
                    <img :src="`/img/class-icons/${group.color}-${group.icon}.svg`" width="32px" height="32px" class="contain border-radius-none">
                </div>
                <div class="d-flex flex-column justify-content-center">
                    <div class="d-flex gap-2" v-if="name">
                        <h5 class="mb-0 text-break">{{ name }}</h5>
                        <i class="fi fi-rr-arrow-right" v-if="child"></i>
                    </div>
                    <small class="mt-1 mb-0" v-if="child">{{ child.class?.name }}</small>
                    <small class="mb-0" v-if="!child && step === 3">
                        {{ selectedChildrenCount + '/' + totalChildrenCount }} {{ trans('general.daily_report.children_selected')}}
                    </small>
                    <div @click.stop class="allergies d-flex flex-wrap align-items-center gap-1" v-if="child && child.allergies?.length > 0">
                        <drop-down-button
                            id="createEditClassModal"
                            cls="p-0"
                            :options="child.allergies"
                            :text="trans('general.page.children.allergies')"
                            icon="fi-rr-angle-small-down"
                            icon-position="right"/>
                    </div>
                </div>
            </div>
            <div v-if="step === 1 && !child && group" @click="changeClass" class="justify-content-end w-100 d-flex align-items-center gap-2" role="button">
                <small class="fw-bold">
                    {{ trans('general.daily_report.change_class') }}
                </small>
                <i class="fi fi-rr-refresh"></i>
            </div>
            <div v-if="step === 2 && group && totalChildrenCount > 0" class="d-flex mb-0 w-100 justify-content-end">
                <div @click="selectAll" role="button" class="d-flex align-items-center gap-2 ">
                    {{ trans('general.module.report.update_popup.step.apply_to.choose_all') }}
                    <i :class="['fi', checkedAll ? 'fi-rr-check-circle' : 'fi-rr-circle']"></i>
                </div>
            </div>
            <div v-if="step === 1 && child" class="text-end">
                <basic-button @click.stop="viewChildReport()" cls="px-1 mb-1 mb-lg-0" :text="trans('general.child.view_today_reports')"/>
            </div>
            <h1 v-if="step === 3 && reportType" :class="['mb-0 d-flex align-items-center', reportType.color]">
                <i :class="['fi', reportType.icon]"></i>
            </h1>
        </div>

        <div v-if="appStatus === 'pending'" class="border-radius bg-gray-100 w-100 py-5 mt-5 mt-lg-2 mb-2 mb-lg-1 d-flex justify-content-center">
            <div>
                <i class="text-gray-300 fi fi-rr-child-head" style="font-size: 100px;"></i>
                <div class="text-gray-600 text-center mt-3 lead">
                    {{ trans('general.child.appearance.expecting') }}...
                </div>
            </div>
        </div>

        <div v-if="step === 1 && child"
             :class="['appearance-text d-flex justify-content-between align-items-center pt-1', {'p-2': appStatus !== 'pending'}]">
            <child-appearance-status
                v-if="step === 1 && child && showChildAppearances"
                :appearances="child.appearances"
                :daily-report="true"
                :full-details="true"
                v-on:status="setStatus($event)"/>

            <attendance-action
                v-if="child.appearance_status?.type !== 'check_out'"
                :daily-report="true"
                v-on:status-changed="statusChanged"
                :child="child"
                :child-id="child.id"
                :status="status(child.appearances).type"/>
        </div>
    </div>
</template>

<script>
import BasicButton from "../../../theme/atoms/BasicButton.vue";
import {eventBus} from "../../../services/EventBus";
import AttendanceAction from "../../../theme/atoms/AttendanceAction.vue";
import ChildAppearanceStatus from "../../../theme/molecules/ChildAppearanceStatus.vue";
import DropDownButton from "../../../theme/atoms/DropDownButton.vue";

export default {
    name: "ReportHeader",
    components: {
        DropDownButton,
        ChildAppearanceStatus,
        AttendanceAction,
        BasicButton
    },
    props: {
        value: {
            type: Boolean,
            required: false
        },
        group: {
            type: Object,
            required: false
        },
        child: {
            type: Object,
            required: false
        },
        step: {
            type: Number,
            required: false
        },
        totalChildrenCount: {
            type: Number,
            required: false,
            default: 0
        },
        selectedChildrenCount: {
            type: Number,
            required: false,
            default: 0
        },
        reportType: {
            type: Object,
            required: false,
        }
    },
    data() {
        return {
            name: this.group?.name ?? this.child.name + ' ' + this.child.surname,
            checkedAll: this.value,
            showChildAppearances: true,
            appStatus: null,
        }
    },
    watch: {
        value: function (value) {
            this.checkedAll = value;
        },
        selectedChildrenCount(val) {
            this.checkedAll = val === this.totalChildrenCount;
        },
        checkedAll(val) {
            this.$emit('input', val);
        }
    },
    methods: {
        setStatus(status) {
            this.appStatus = status;
            this.appStatus === 'pending' ? this.showChildAppearances = false : '';
        },
        selectAll() {
            this.checkedAll = !this.checkedAll;
            this.$emit('select-all', this.checkedAll);
        },
        changeClass() {
            this.$emit('change-class');
        },
        handleHeaderClick() {
            this.viewChildProfile();
        },
        viewChildProfile() {
            if(this.child) {
                window.location.href = '/children/read/' + this.child.id;
            }
        },
        statusChanged(data) {
            this.$emit('close');
            eventBus.$emit('reloadChildren', data);
        },
        status(appearances) {
            let types = {
                check_in: {
                    title: trans('general.module.attendance.check_in'),
                    description: 'Teacher name',
                    color: 'text-green',
                    type: 'check_in'
                },
                check_out: {
                    title: trans('general.module.attendance.checked_out'),
                    description: 'Teacher name',
                    color: 'text-red',
                    type: 'check_out'
                },
                missing: {
                    title: trans('general.module.attendance.missing'),
                    description: 'Teacher name',
                    color: 'text-pink',
                    type: 'missing'
                },
                pending: {
                    title: trans('general.module.attendance.pending'),
                    description: trans('general.module.report.status_alert.pending'),
                    color: 'text-orange',
                    type: 'pending'
                }
            }
            let status = types.pending;
            appearances.forEach(val => {
                if (types[val.type] && !val.parent_id) {
                    status = types[val.type];
                    status.description = val.type === 'missing' ? val.description : val.user ? (val.user.name + ' ' + val.user.surname) : '';
                } else {
                    if (val.type === 'check_in' && !['pending', 'missing'].includes(status.type)) {
                        return;
                    }
                    // status = types.pending;
                    status.description = val.description
                        ? `${val.parent.name} : <b class="fw-900 text-dark">${val.description}</b>`
                        : val.parent.name + ' ' + val.parent.surname
                    status.parent_id = val.parent_id;
                }
            })

            return status;
        },
        viewChildReport() {
            this.$emit('close');
            eventBus.$emit('view-child-report', {childId: this.child?.id, status: this.child?.appearance_status?.type});
        },
    }
}
</script>

<style>
#drop-btn-createEditClassModal {
    height: unset;
    border: 0;
    color: #FF6767;
}
</style>
